import { NotificationSource } from './notification-source'
import type { NotificationHandler } from './notification-handler'
import { NotificationHandlerEventType } from './notification-handler-event'
import type { NotificationHandlerFactory } from './notification-handler-factory'
import { WebSocketNotificationHandlerFactory } from './websocket-notification-handler'
import { destroyChannelInfo, getChannelInfo, updateChannelInfoLifeTime } from './channel-info-service'
import type { NotificationChannel } from './model'
import type { NotificationChannelEvent } from '../notification-channel/models'
import { getShellLogger } from '../../common/logger'

export class NotificationChannelImpl implements NotificationChannel {
  private notificationSource: NotificationSource = NotificationSource.WEBSOCKET
  private notificationHandler: NotificationHandler | null = null

  constructor(private handlerFactory: NotificationHandlerFactory, private externalUserKey: string) {}

  public start() {
    this.initializeHandler().then(() => {
      this.notificationHandler?.connect()
    })
  }

  public get channelInfo() {
    return this.notificationHandler?.channelInfo
  }

  public get isConnected(): boolean {
    return !!this.notificationHandler?.isConnected
  }

  public get id() {
    return this.notificationHandler?.channelInfo.channelId ?? ''
  }

  public setNotificationSource(notificationSource: NotificationSource) {
    this.clearHandler()
    this.notificationSource = notificationSource
    switch (this.notificationSource) {
      case NotificationSource.WEBSOCKET:
        this.handlerFactory = WebSocketNotificationHandlerFactory
        break
      case NotificationSource.PUSH:
        throw new Error('push notification are not supported yet')
    }
    this.initializeHandler()
  }

  public subscribe<T>(eventListener: (event: NotificationChannelEvent<T>) => void): void {
    this.notificationHandler?.addListener(NotificationHandlerEventType.MESSAGE, eventListener)
  }

  public unsubscribe<T>(eventListener: (event: NotificationChannelEvent<T>) => void): void {
    this.notificationHandler?.removeListener(NotificationHandlerEventType.MESSAGE, eventListener)
  }

  private async initializeHandler() {
    const channelInfo = await getChannelInfo(this.externalUserKey)
    this.notificationHandler = this.handlerFactory(channelInfo)
    this.notificationHandler.addListener(
      NotificationHandlerEventType.UPDATE_CHANNEL_LIFETIME,
      this.handleUpdateChannelLifetime,
    )
    this.notificationHandler.addListener(
      NotificationHandlerEventType.TOO_MANY_ATTEMPTS,
      this.handleTooManyAttemptsEvent,
    )
    window.addEventListener('online', this.handleWindowOnlineEvent)
    window.addEventListener('offline', this.handleWindowOfflineEvent)
  }

  private internalRestart() {
    this.clearHandler()
    this.destroyChannelInfo()
    this.start()
  }

  private clearHandler() {
    if (this.notificationHandler) {
      this.notificationHandler.removeAllListeners()
      window.removeEventListener('online', this.handleWindowOnlineEvent)
      window.removeEventListener('offline', this.handleWindowOfflineEvent)
      this.notificationHandler = null
    }
  }

  private handleWindowOnlineEvent = () => {
    this.notificationHandler?.resume()
    this.notificationHandler?.emit(NotificationHandlerEventType.OPEN)
    getShellLogger().log('WebSocket:You are now connected to the network')
  }

  private handleWindowOfflineEvent = () => {
    this.notificationHandler?.pause()
    this.notificationHandler?.emit(NotificationHandlerEventType.CLOSE, {
      errorCode: 'ERR_INTERNET_DISCONNECTED',
      message: 'No Internet',
    })
  }

  private handleTooManyAttemptsEvent = (event: Event) => {
    getShellLogger().error('WebSocket:Connection TooManyAttempts', event)
    this.internalRestart()
  }

  private handleUpdateChannelLifetime = () => {
    updateChannelInfoLifeTime(this.externalUserKey, this.id)
      .then(() => {
        getShellLogger().log('WebSocket:Lifetime updated')
      })
      .catch((error: Error) => {
        getShellLogger().error('WebSocket:Lifetime failed to update', error)
        this.internalRestart()
      })
  }

  private destroyChannelInfo() {
    destroyChannelInfo(this.externalUserKey).catch(error => {
      getShellLogger().error('WebSocket:Failed to remove previous notification channel', error)
    })
  }
}
